/**
 * German text overrides for the built-in heat pump simulation quests.
 *
 * These overrides are applied only for the built-in quest flows when the
 * active UI language is German. Training-profile quests remain untouched.
 * Relevant technical wording follows DIN/TS 12831-1 style terminology.
 */

export const GERMAN_SHARED_QUEST_OVERRIDES = [
  {
    id: 1,
    title: "System kennenlernen",
    category: "Grundlagen",
    difficulty: "Anfänger",
    learningObjectives: [
      "Den Aufbau der Wärmepumpensimulation verstehen",
      "Außeneinheit, Raumeinheit und Bedienfeld sicher erkennen",
      "Temperaturverläufe und Energieflüsse in Echtzeit beobachten",
      "Heizkörper- und Wärmestrom-Visualisierung gezielt nutzen",
      "Ventil- und Thermostatfunktion grundlegend verstehen"
    ],
    scenario: {
      context: "Sie lernen den Betrieb einer modernen Luft-Wasser-Wärmepumpe kennen. Vor jeder Optimierung sollten Sie das System zunächst im normalen Betrieb verstehen.",
      realWorldConnection: "In der Praxis verhindert ein gutes Systemverständnis teure Fehlentscheidungen und schafft eine saubere Grundlage für jede spätere Einstellung."
    },
    steps: [
      {
        id: "1a",
        task: "Simulation starten",
        instruction: "Klicken Sie in der Werkzeugleiste auf die Starttaste, um den Wärmepumpenbetrieb zu beginnen.",
        detailedGuidance: "Nach dem Start berechnet die Simulation Wärmeübertragung, Temperaturänderungen und Effizienzwerte fortlaufend. Dadurch werden die Systemzusammenhänge direkt sichtbar.",
        successIndicator: "Die Simulation läuft und Temperaturwerte ändern sich sichtbar."
      },
      {
        id: "1b",
        task: "Temperaturentwicklung beobachten",
        instruction: "Beobachten Sie, wie sich die Raumtemperatur verändert und wie Heizleistung und Wärmeverluste zusammenwirken.",
        detailedGuidance: "Steigt die abgegebene Heizleistung über die Verluste, erwärmt sich der Raum. Übersteigen die Verluste die Heizleistung, sinkt die Raumtemperatur.",
        successIndicator: "Sie können die Veränderung der Raumtemperatur nachvollziehen."
      },
      {
        id: "1c",
        task: "Außentemperatur finden",
        instruction: "Suchen Sie in der Raumdarstellung die Außentemperaturanzeige.",
        detailedGuidance: "Die Außentemperatur beeinflusst direkt die Temperaturdifferenz zwischen innen und außen und damit die Höhe der Wärmeverluste.",
        successIndicator: "Sie können den aktuellen Außentemperaturwert ablesen."
      },
      {
        id: "1d",
        task: "Bedienfeld lokalisieren",
        instruction: "Suchen Sie rechts das Bedienfeld mit den wichtigsten Einstellmöglichkeiten.",
        detailedGuidance: "Dort stellen Sie unter anderem Außentemperatur, Raumtyp, Vorlauftemperatur, Heizkörper, Gebäudehülle und Lüftung ein.",
        successIndicator: "Sie können das Bedienfeld und seine Hauptbereiche benennen."
      },
      {
        id: "1e",
        task: "COP-Anzeige erkennen",
        instruction: "Suchen Sie an der Außeneinheit die COP-Anzeige.",
        detailedGuidance: "Der COP zeigt, wie effizient die Wärmepumpe elektrische Energie in nutzbare Heizwärme umsetzt.",
        successIndicator: "Sie können den COP an der Außeneinheit finden und ablesen."
      },
      {
        id: "1f",
        task: "Heizkörperanzeige aktivieren",
        instruction: "Blenden Sie über die Werkzeugleiste die Heizkörperleistungsanzeige ein.",
        detailedGuidance: "So sehen Sie, wie sich die Heizkörperleistung mit Bauart, Größe und Vorlauftemperatur verändert.",
        successIndicator: "Die Heizkörperleistungsanzeige ist sichtbar."
      },
      {
        id: "1g",
        task: "Wärmestrom-Overlay prüfen",
        instruction: "Aktivieren Sie das Wärmestrom-Overlay, um Wärmeverluste über die Hülle sichtbar zu machen.",
        detailedGuidance: "Die Darstellung hilft dabei, thermische Schwachstellen und die Richtung des Wärmetransports schnell zu erkennen.",
        successIndicator: "Sie sehen Wärmeströme in der Raumdarstellung."
      },
      {
        id: "1h",
        task: "Ventilsystem verstehen",
        instruction: "Suchen Sie Voreinstellventil und Thermostatkopf am Heizkörper.",
        detailedGuidance: "Das Voreinstellventil begrenzt den maximalen Durchfluss. Der Thermostatkopf regelt den Durchfluss abhängig von der Raumtemperatur nach.",
        successIndicator: "Sie können beide Bauteile am Heizkörper identifizieren."
      }
    ],
    completionCriteria: {
      description: "Systemstart und Beobachtung des Grundverhaltens",
      conditions: ["Simulation läuft"]
    },
    knowledgeSynthesis: {
      keyTakeaways: [
        "Wärmepumpensysteme gleichen Heizleistung und Wärmeverluste fortlaufend aus",
        "Der COP ist die zentrale Effizienzkennzahl",
        "Vor jeder Optimierung ist die Beobachtung des Ist-Betriebs wichtig",
        "Visualisierungen helfen beim schnellen Verständnis des Systems"
      ],
      formulaHighlight: "COP = Q_Heiz / W_el"
    }
  }
];

export const GERMAN_USER_TYPE_QUEST_OVERRIDES = {
  installer: [
    {
      id: 2,
      title: "Auslegungsbedingungen festlegen",
      category: "Installationsgrundlagen",
      difficulty: "Anfänger",
      learningObjectives: [
        "Normaußentemperatur und Norm-Innentemperatur verstehen",
        "Auslegungsrandbedingungen nach DIN/TS 12831-1 einordnen",
        "Die Temperaturdifferenz als Grundlage der Heizlast begreifen"
      ],
      scenario: {
        context: "Sie bereiten die Installation eines Heizsystems für ein Wohngebäude vor. Die Anlage muss auch im Auslegungsfall die gewünschte Norm-Innentemperatur erreichen.",
        realWorldConnection: "Falsche Randbedingungen führen zu falscher Auslegung, unnötigen Reklamationen und vermeidbarer Nacharbeit."
      },
      steps: [
        {
          id: "2a",
          task: "Normaußentemperatur einstellen",
          instruction: "Setzen Sie die Außentemperatur auf -10 °C oder wählen Sie eine Stadt mit passender Normaußentemperatur.",
          detailedGuidance: "Die Normaußentemperatur ist standortabhängig und beschreibt den kalten Auslegungsfall, für den die Anlage dimensioniert wird.",
          successIndicator: "Die Außentemperatur liegt bei -10 °C oder darunter."
        },
        {
          id: "2b",
          task: "Raumtyp Wohnen wählen",
          instruction: "Wählen Sie den Raumtyp Wohnen, damit eine Soll-Raumtemperatur von 20 °C angesetzt wird.",
          detailedGuidance: "Je nach Raumnutzung gelten unterschiedliche Norm-Innentemperaturen. Aufenthaltsräume werden typischerweise mit 20 °C angesetzt.",
          successIndicator: "Raumtyp Wohnen und 20 °C Sollwert sind eingestellt."
        },
        {
          id: "2c",
          task: "Temperaturdifferenz bestimmen",
          instruction: "Bestimmen Sie bei -10 °C außen und 20 °C innen die Auslegungs-Temperaturdifferenz.",
          detailedGuidance: "Die Temperaturdifferenz ΔT treibt Transmissionswärmeverlust und Lüftungswärmeverlust und ist damit direkt für die Heizlast relevant.",
          successIndicator: "Sie können den Auslegungsfall mit ΔT = 30 K erklären."
        }
      ],
      completionCriteria: {
        description: "Normgerechte Randbedingungen einstellen",
        conditions: [
          "Außentemperatur ≤ -10°C",
          "Raumtyp Wohnen gewählt",
          "Soll-Raumtemperatur = 20°C"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Normaußentemperatur und Norm-Innentemperatur definieren den Auslegungsfall",
          "Die Temperaturdifferenz ΔT prägt die Heizlast wesentlich",
          "Standort und Raumnutzung müssen in der Auslegung korrekt berücksichtigt werden"
        ],
        formulaHighlight: "ΔT = T_innen - T_außen"
      }
    },
    {
      id: 3,
      title: "Heizflächenanpassung für die Wärmepumpen-Nachrüstung",
      category: "Praktische Installation",
      difficulty: "Mittelstufe",
      learningObjectives: [
        "Den Einfluss der Vorlauftemperatur auf den COP verstehen",
        "Die Eignung vorhandener Heizkörper für 45 °C Vorlauf bewerten",
        "Mit der Wärmebilanz eine sinnvolle Heizflächenanpassung ableiten"
      ],
      scenario: {
        context: "Ein vorhandenes Kesselsystem soll durch eine Wärmepumpe ersetzt werden. Sie prüfen, ob die bestehenden Heizkörper bei 45 °C Vorlauf ausreichen.",
        realWorldConnection: "Diese Bewertung ist in Sanierungsprojekten zentral, weil sie Komfort, Effizienz und Kosten direkt beeinflusst."
      },
      steps: [
        {
          id: "3a",
          task: "Sanierungsszenario einstellen",
          instruction: "Wählen Sie -10 °C außen, Raumtyp Wohnen mit 20 °C und einen U-Wert von etwa 0,4 W/(m²K).",
          detailedGuidance: "Damit bilden Sie ein typisches modernisiertes Bestandsgebäude mit realistischer Heizlast nach.",
          successIndicator: "Die Randbedingungen des Sanierungsszenarios sind eingestellt."
        },
        {
          id: "3b",
          task: "Mit Typ 21 starten",
          instruction: "Wählen Sie einen Typ-21-Heizkörper mit mittlerer Größe und notieren Sie die Norm-Nennwärmeleistung.",
          detailedGuidance: "Die Norm-Nennwärmeleistung bezieht sich auf hohe Referenztemperaturen und ist nur bedingt auf den Wärmepumpenbetrieb übertragbar.",
          successIndicator: "Typ 21 ist ausgewählt und die Nennwärmeleistung ist sichtbar."
        },
        {
          id: "3c",
          task: "Kesselbetriebspunkt einstellen",
          instruction: "Setzen Sie die Vorlauftemperatur auf 75 °C.",
          detailedGuidance: "Das bildet den klassischen Hochtemperaturbetrieb eines Kesselsystems ab und dient als Vergleichsbasis.",
          successIndicator: "Die Vorlauftemperatur beträgt 75 °C."
        },
        {
          id: "3d",
          task: "COP im Hochtemperaturbetrieb notieren",
          instruction: "Lesen Sie den COP an der Außeneinheit ab und notieren Sie ihn.",
          detailedGuidance: "Hohe Vorlauftemperaturen verschlechtern den COP deutlich und zeigen, warum Wärmepumpen von niedrigen Systemtemperaturen profitieren.",
          successIndicator: "Der COP bei 75 °C ist dokumentiert."
        },
        {
          id: "3e",
          task: "Ziel-Vorlauf 45 °C setzen",
          instruction: "Reduzieren Sie die Vorlauftemperatur auf 45 °C.",
          detailedGuidance: "45 °C ist ein praxisnaher Zielwert für einen effizienten Wärmepumpenbetrieb im Bestand.",
          successIndicator: "Die Vorlauftemperatur liegt bei 45 °C."
        },
        {
          id: "3f",
          task: "Wärmebilanz prüfen",
          instruction: "Prüfen Sie, ob die Wärmebilanz mit Typ 21 positiv oder negativ ist.",
          detailedGuidance: "Die Wärmebilanz ergibt sich aus Heizkörperleistung minus Transmissions- und Lüftungswärmeverlust. Eine negative Bilanz bedeutet Unterdeckung.",
          successIndicator: "Sie können beurteilen, ob Typ 21 ausreichend ist."
        },
        {
          id: "3g",
          task: "Typ 22 testen",
          instruction: "Wechseln Sie auf Typ 22 und beobachten Sie die Veränderung der Wärmebilanz.",
          detailedGuidance: "Mehr Heizfläche verbessert die Wärmeabgabe bei gleicher Vorlauftemperatur.",
          successIndicator: "Die Wirkung des Wechsels auf Typ 22 ist sichtbar."
        },
        {
          id: "3h",
          task: "Bei Bedarf Typ 33 wählen",
          instruction: "Wenn die Bilanz weiter negativ ist, testen Sie Typ 33.",
          detailedGuidance: "Typ 33 bietet von den Standard-Plattenheizkörpern die größte Wärmeabgabe bei gleicher Länge.",
          successIndicator: "Sie können beurteilen, ob Typ 33 ausreicht."
        },
        {
          id: "3i",
          task: "Kleinste ausreichende Lösung finden",
          instruction: "Kombinieren Sie Typ und Größe so, dass bei 45 °C eine positive Wärmebilanz entsteht.",
          detailedGuidance: "Gesucht ist die kleinste wirtschaftlich sinnvolle Heizflächenanpassung, nicht die maximal mögliche Reserve.",
          successIndicator: "Eine passende Heizflächenkonfiguration ist gefunden."
        },
        {
          id: "3j",
          task: "Effizienzgewinn bewerten",
          instruction: "Vergleichen Sie den COP bei 45 °C mit dem dokumentierten Wert bei 75 °C.",
          detailedGuidance: "Dieser Vergleich zeigt den eigentlichen Nutzen der Heizflächenanpassung: bessere Effizienz bei ausreichender Wärmeabgabe.",
          successIndicator: "Sie können den COP-Gewinn fachlich begründen."
        }
      ],
      completionCriteria: {
        description: "Heizflächen für 45 °C Vorlaufbetrieb passend auslegen",
        conditions: [
          "Vorlauftemperatur ≤ 45°C",
          "Wärmebilanz positiv oder annähernd ausgeglichen"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Niedrigere Vorlauftemperaturen verbessern den COP",
          "Die Wärmebilanz zeigt sofort, ob die Heizfläche ausreicht",
          "Heizflächenanpassung ist oft der Schlüssel zum effizienten Sanierungsbetrieb"
        ],
        formulaHighlight: "Wärmebilanz = Q_Heizkörper - Q_Trans - Q_Lüftung"
      }
    },
    {
      id: 4,
      title: "Hydraulischer Abgleich",
      category: "Inbetriebnahme",
      difficulty: "Mittelstufe",
      learningObjectives: [
        "Den hydraulischen Abgleich verstehen",
        "Die Voreinstellung von Ventilen sinnvoll anpassen",
        "Durchfluss und Wärmeübertragung gemeinsam beurteilen"
      ],
      scenario: {
        context: "Nach der Installation muss das System so abgeglichen werden, dass ausreichend Wärme anliegt, aber keine unnötige Pumpenarbeit entsteht.",
        realWorldConnection: "Ein fehlender hydraulischer Abgleich führt oft zu Geräuschen, schlechter Wärmeverteilung und unnötigem Stromverbrauch."
      },
      steps: [
        {
          id: "4a",
          task: "Aktuelle Ventilstellung prüfen",
          instruction: "Lesen Sie die aktuelle Stellung des Voreinstellventils ab.",
          detailedGuidance: "Das Voreinstellventil begrenzt den maximalen Durchfluss und ist damit ein zentrales Werkzeug für den hydraulischen Abgleich.",
          successIndicator: "Die aktuelle Ventilstellung ist bekannt."
        },
        {
          id: "4b",
          task: "Voreinstellung schrittweise reduzieren",
          instruction: "Reduzieren Sie die Voreinstellung in kleinen Schritten und beobachten Sie die Heizleistung.",
          detailedGuidance: "Solange die Wärmeabgabe ausreicht, spart ein geringerer Durchfluss Pumpenenergie und verbessert häufig das Anlagenverhalten.",
          successIndicator: "Sie haben unterschiedliche Ventilstellungen getestet."
        },
        {
          id: "4c",
          task: "Niedrigste ausreichende Stellung finden",
          instruction: "Suchen Sie die kleinste Ventilstellung, bei der die Heizleistung weiterhin ausreicht.",
          detailedGuidance: "Ziel ist ein minimaler Durchfluss bei ausreichender Raumversorgung.",
          successIndicator: "Eine niedrige, aber ausreichende Stellung ist gefunden."
        },
        {
          id: "4d",
          task: "Heizleistung absichern",
          instruction: "Prüfen Sie, ob die Heizleistung trotz reduzierter Stellung mindestens 90 % der Verluste deckt.",
          detailedGuidance: "Damit stellen Sie sicher, dass der Abgleich nicht zulasten der Raumversorgung geht.",
          successIndicator: "Die Leistung bleibt trotz Abgleich ausreichend."
        }
      ],
      completionCriteria: {
        description: "Hydraulischen Abgleich mit ausreichender Wärmeabgabe erreichen",
        conditions: [
          "Ventilstellung ≤ 3",
          "Heizleistung ≥ 90% der Gesamtverluste"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Hydraulischer Abgleich verbessert Effizienz und Komfort",
          "Geringerer Durchfluss kann sinnvoll sein, solange die Leistung ausreicht",
          "Ein abgeglichenes System arbeitet oft leiser und wirtschaftlicher"
        ],
        formulaHighlight: "Q = ṁ × c_p × ΔT"
      }
    },
    {
      id: 5,
      title: "Einfluss der Lüftung bewerten",
      category: "Systemverhalten",
      difficulty: "Mittelstufe",
      learningObjectives: [
        "Lüftungswärmeverluste einordnen",
        "Hohe Luftwechselraten als Problem erkennen",
        "Sinnvolle Hinweise zu Luftdichtheit und Lüftung geben"
      ],
      scenario: {
        context: "Ein Kunde meldet unzureichende Raumtemperaturen. Sie prüfen, ob hohe Lüftungswärmeverluste statt einer zu kleinen Wärmepumpe die Ursache sind.",
        realWorldConnection: "Viele vermeintliche Wärmepumpenprobleme gehen in Wahrheit auf Undichtheiten oder ungünstige Lüftung zurück."
      },
      steps: [
        {
          id: "5a",
          task: "Lüftungsverluste vergleichen",
          instruction: "Vergleichen Sie Lüftungswärmeverlust und Transmissionswärmeverlust.",
          detailedGuidance: "Gerade in gut gedämmten Gebäuden kann der Lüftungswärmeverlust einen überraschend großen Anteil ausmachen.",
          successIndicator: "Sie können die Verlustanteile vergleichen."
        },
        {
          id: "5b",
          task: "Hohe Luftwechselrate simulieren",
          instruction: "Erhöhen Sie den Luftwechsel deutlich und beobachten Sie die Auswirkungen.",
          detailedGuidance: "Ein hoher Luftwechsel treibt die Heizlast nach oben und kann die erreichbare Raumtemperatur begrenzen.",
          successIndicator: "Die Wirkung einer hohen Luftwechselrate ist sichtbar."
        },
        {
          id: "5c",
          task: "Auf plausiblen Wert zurückgehen",
          instruction: "Reduzieren Sie den Luftwechsel wieder auf einen plausiblen Wert unter 1,25 ACH.",
          detailedGuidance: "Damit lässt sich zeigen, wie stark schon einfache Verbesserungen die Heizlast verringern können.",
          successIndicator: "Der Luftwechsel liegt unter 1,25 ACH."
        },
        {
          id: "5d",
          task: "Energieeffekt benennen",
          instruction: "Beschreiben Sie die Veränderung des Lüftungswärmeverlusts durch den geringeren Luftwechsel.",
          detailedGuidance: "So wird der Nutzen von Luftdichtheit oder einer geordneten Lüftungsstrategie unmittelbar greifbar.",
          successIndicator: "Sie können den energetischen Effekt verständlich erklären."
        }
      ],
      completionCriteria: {
        description: "Luftwechsel energetisch verbessern",
        conditions: ["Luftwechselrate < 1.25 ACH"]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Lüftungswärmeverluste können sehr groß werden",
          "Hoher Luftwechsel erhöht die Heizlast unmittelbar",
          "Luftdichtheit und Lüftungskonzept sind für die Systemleistung wichtig"
        ],
        formulaHighlight: "Q_Lüftung ∝ n × ΔT"
      }
    },
    {
      id: 6,
      title: "Inbetriebnahme verifizieren",
      category: "Qualitätssicherung",
      difficulty: "Fortgeschritten",
      learningObjectives: [
        "Eine vollständige Inbetriebnahmekontrolle durchführen",
        "Leistung und Effizienz gegen den Auslegungsfall prüfen",
        "Wichtige Ergebnisse für die Übergabe dokumentieren"
      ],
      scenario: {
        context: "Die Anlage ist montiert. Vor der Übergabe an den Kunden prüfen Sie Leistung, Temperaturhaltung und Effizienz im Auslegungsfall.",
        realWorldConnection: "Eine saubere Inbetriebnahme schafft Sicherheit, reduziert Reklamationen und dokumentiert professionelle Arbeit."
      },
      steps: [
        {
          id: "6a",
          task: "Referenzfall einstellen",
          instruction: "Stellen Sie -10 °C außen und 20 °C im Wohnraum ein.",
          detailedGuidance: "So prüfen Sie die Anlage unter normnahen Randbedingungen.",
          successIndicator: "Die Referenzbedingungen sind gesetzt."
        },
        {
          id: "6b",
          task: "Leistungsdeckung prüfen",
          instruction: "Kontrollieren Sie, ob die Heizleistung die Gesamtverluste übersteigt.",
          detailedGuidance: "Nur dann ist sichergestellt, dass die Anlage im Auslegungsfall ausreichend dimensioniert ist.",
          successIndicator: "Die Heizleistung deckt die Gesamtverluste."
        },
        {
          id: "6c",
          task: "Vorlauftemperatur bewerten",
          instruction: "Prüfen Sie, ob eine niedrige Vorlauftemperatur möglich bleibt.",
          detailedGuidance: "Ein effizienter Wärmepumpenbetrieb setzt möglichst niedrige Systemtemperaturen voraus.",
          successIndicator: "Die Vorlauftemperatur liegt in einem günstigen Bereich."
        },
        {
          id: "6d",
          task: "COP kontrollieren",
          instruction: "Vergleichen Sie den COP mit einem guten Zielwert für den Auslegungsfall.",
          detailedGuidance: "Ein hoher COP bei ungünstigen Randbedingungen spricht für eine stimmige Systemabstimmung.",
          successIndicator: "Der COP liegt im gewünschten Bereich."
        },
        {
          id: "6e",
          task: "Temperaturstabilität prüfen",
          instruction: "Prüfen Sie abschließend, ob die Raumtemperatur nahe am Sollwert bleibt.",
          detailedGuidance: "Eine stabile Raumtemperatur zeigt, dass Leistung und Regelung zusammenpassen.",
          successIndicator: "Die Raumtemperatur bleibt nahe dem Sollwert."
        }
      ],
      completionCriteria: {
        description: "Inbetriebnahme erfolgreich bestätigen",
        conditions: [
          "COP ≥ 4.5",
          "Raumtemperatur innerhalb von 2°C um den Sollwert"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Die Inbetriebnahme überprüft, ob die Auslegung in der Praxis aufgeht",
          "Leistung, Vorlauftemperatur und COP müssen zusammen betrachtet werden",
          "Temperaturstabilität ist ein wichtiger Qualitätsindikator"
        ],
        formulaHighlight: "COP_System = Q_abgegeben / W_el"
      }
    }
  ],
  energyConsultant: [
    {
      id: 2,
      title: "Normgerechte Randbedingungen",
      category: "Regelwerk",
      difficulty: "Anfänger",
      learningObjectives: [
        "Auslegungsbedingungen nach DIN/TS 12831-1 verstehen",
        "Normaußen- und Norm-Innentemperaturen in der Heizlast bewerten",
        "Den Referenzfall für eine fachliche Analyse herstellen"
      ],
      scenario: {
        context: "Sie bereiten eine energetische Bewertung vor und benötigen dafür einen normgerechten Auslegungsfall als Referenz.",
        realWorldConnection: "Fachlich belastbare Empfehlungen setzen saubere und nachvollziehbare Randbedingungen voraus."
      },
      steps: [
        {
          id: "2a",
          task: "Normaußentemperatur setzen",
          instruction: "Setzen Sie die Außentemperatur auf -10 °C oder einen passenden regionalen Referenzwert.",
          detailedGuidance: "Die Normaußentemperatur ist der kalte Bemessungsfall und standortabhängig.",
          successIndicator: "Die Außentemperatur liegt im Auslegungsbereich."
        },
        {
          id: "2b",
          task: "Norm-Innentemperatur zuordnen",
          instruction: "Wählen Sie den Raumtyp Wohnen mit 20 °C Sollwert.",
          detailedGuidance: "Die Raumnutzung bestimmt die angesetzte Norm-Innentemperatur und damit den Heizlastansatz.",
          successIndicator: "20 °C im Wohnraum sind eingestellt."
        },
        {
          id: "2c",
          task: "ΔT erläutern",
          instruction: "Bestimmen Sie die Temperaturdifferenz zwischen innen und außen.",
          detailedGuidance: "Die Differenz zwischen Norm-Innentemperatur und Normaußentemperatur treibt die Verluste.",
          successIndicator: "Sie können den Referenzfall mit ΔT erklären."
        }
      ],
      completionCriteria: {
        description: "Normgerechten Referenzfall herstellen",
        conditions: [
          "Außentemperatur ≤ -10°C",
          "Raumtyp Wohnen gewählt",
          "Soll-Raumtemperatur = 20°C"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Der Auslegungsfall ist normativ definiert",
          "Normaußentemperatur und Norm-Innentemperatur bestimmen ΔT",
          "Ohne saubere Randbedingungen ist keine belastbare Bewertung möglich"
        ],
        formulaHighlight: "ΔT = T_innen - T_außen"
      }
    },
    {
      id: 3,
      title: "Analyse der Gebäudehülle",
      category: "Energetische Bewertung",
      difficulty: "Mittelstufe",
      learningObjectives: [
        "U-Werte fachlich einordnen",
        "Transmissionswärmeverluste berechnen",
        "Verbesserungspotenziale der Hülle erkennen"
      ],
      scenario: {
        context: "Sie bewerten die Gebäudehülle eines Bestandsgebäudes, um die Eignung für einen effizienten Wärmepumpenbetrieb abzuschätzen.",
        realWorldConnection: "Eine bessere Hülle senkt die Heizlast oft wirkungsvoller als eine reine Vergrößerung der Anlagentechnik."
      },
      steps: [
        {
          id: "3a",
          task: "Bauteilparameter öffnen",
          instruction: "Öffnen Sie die U-Parameter für Dach, Wand und Fenster.",
          detailedGuidance: "Hier legen Sie die wärmeübertragenden Flächen und U-Werte fest.",
          successIndicator: "Der Dialog zur Gebäudehülle ist geöffnet."
        },
        {
          id: "3b",
          task: "Gut gedämmte Wand einstellen",
          instruction: "Stellen Sie für die Wand 50 m² und einen U-Wert von 0,2 W/(m²·K) ein.",
          detailedGuidance: "So entsteht ein energetisch guter Referenzzustand mit niedrigen Transmissionswärmeverlusten.",
          successIndicator: "Wandfläche und U-Wert sind passend gesetzt."
        },
        {
          id: "3c",
          task: "Transmissionswärmeverlust berechnen",
          instruction: "Berechnen Sie den Transmissionswärmeverlust mit Q = U × A × ΔT.",
          detailedGuidance: "So verknüpfen Sie die Eingabegrößen direkt mit der physikalischen Wirkung.",
          successIndicator: "Sie können den Wärmeverlust rechnerisch nachvollziehen."
        },
        {
          id: "3d",
          task: "Wärmestrom anzeigen",
          instruction: "Schalten Sie das Wärmestrom-Overlay ein.",
          detailedGuidance: "Die Visualisierung macht sichtbar, wo die Hülle besonders hohe Verluste verursacht.",
          successIndicator: "Das Wärmestrom-Overlay ist aktiv."
        }
      ],
      completionCriteria: {
        description: "Gut gedämmten Hüllzustand einstellen",
        conditions: [
          "Wandfläche etwa 50 m²",
          "Wand-U-Wert etwa 0,2 W/(m²·K)"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Der U-Wert beschreibt den Wärmedurchgang eines Bauteils",
          "Transmissionswärmeverluste hängen von U, Fläche und ΔT ab",
          "Eine gute Hülle erleichtert niedrige Vorlauftemperaturen"
        ],
        formulaHighlight: "Q_Trans = U × A × ΔT"
      }
    },
    {
      id: 4,
      title: "Energetische Wirkung der Lüftung",
      category: "Energieanalyse",
      difficulty: "Mittelstufe",
      learningObjectives: [
        "Lüftungswärmeverluste quantifizieren",
        "Den Einfluss des Luftwechsels einordnen",
        "Potenziale der Wärmerückgewinnung bewerten"
      ],
      scenario: {
        context: "Ein Gebäude ist gut gedämmt, zeigt aber dennoch hohe Verbräuche. Sie prüfen die Lüftung als möglichen Hauptverlustpfad.",
        realWorldConnection: "Gerade in gut gedämmten Gebäuden verschiebt sich der Fokus oft von der Hülle zur Lüftung."
      },
      steps: [
        {
          id: "4a",
          task: "Lüftungswärmeverlust einordnen",
          instruction: "Vergleichen Sie Lüftungs- und Transmissionswärmeverlust.",
          detailedGuidance: "So erkennen Sie, welcher Verlustpfad energetisch dominiert.",
          successIndicator: "Die Verlustanteile sind fachlich eingeordnet."
        },
        {
          id: "4b",
          task: "Luftwechsel variieren",
          instruction: "Vergleichen Sie einen hohen mit einem niedrigen Luftwechsel.",
          detailedGuidance: "Der Luftwechsel beeinflusst den Lüftungswärmeverlust nahezu linear.",
          successIndicator: "Die Wirkung unterschiedlicher Luftwechselraten ist sichtbar."
        },
        {
          id: "4c",
          task: "Sinnvollen Luftwechsel einstellen",
          instruction: "Stellen Sie einen plausiblen Luftwechsel unter 1,25 ACH ein.",
          detailedGuidance: "Damit bilden Sie ein modernisiertes oder sorgfältig genutztes Gebäude realistischer ab.",
          successIndicator: "Der Luftwechsel ist auf ein plausibles Niveau reduziert."
        },
        {
          id: "4d",
          task: "Wärmerückgewinnung abschätzen",
          instruction: "Schätzen Sie den Effekt einer Wärmerückgewinnung auf den Lüftungswärmeverlust ab.",
          detailedGuidance: "So wird sichtbar, welche Rolle Lüftungstechnik im Gesamtkonzept spielen kann.",
          successIndicator: "Sie können das Einsparpotenzial benennen."
        }
      ],
      completionCriteria: {
        description: "Lüftung energetisch optimieren",
        conditions: ["Luftwechselrate < 1.25 ACH"]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Lüftungswärmeverluste können dominant werden",
          "Der Luftwechsel ist eine zentrale Einflussgröße",
          "Wärmerückgewinnung kann den Verlust deutlich reduzieren"
        ],
        formulaHighlight: "Q_Lüftung ∝ n × ΔT"
      }
    },
    {
      id: 5,
      title: "COP-Optimierung analysieren",
      category: "Effizienzoptimierung",
      difficulty: "Fortgeschritten",
      learningObjectives: [
        "Die Wirkung der Vorlauftemperatur auf den COP verstehen",
        "Effizienzgewinne quantifizieren",
        "Optimierte Betriebspunkte ableiten"
      ],
      scenario: {
        context: "Sie möchten einem Kunden zeigen, wie stark die Vorlauftemperatur den COP und damit die Betriebskosten beeinflusst.",
        realWorldConnection: "Eine fachlich saubere COP-Analyse schafft Transparenz und erhöht die Qualität der Empfehlung."
      },
      steps: [
        {
          id: "5a",
          task: "Basis-COP aufnehmen",
          instruction: "Stellen Sie einen höheren Vorlauf ein und notieren Sie den COP.",
          detailedGuidance: "Das ist der Ausgangspunkt für den Vergleich mit einem optimierten Niedertemperaturbetrieb.",
          successIndicator: "Ein Referenz-COP ist dokumentiert."
        },
        {
          id: "5b",
          task: "Vorlauf absenken",
          instruction: "Reduzieren Sie die Vorlauftemperatur deutlich.",
          detailedGuidance: "Mit sinkender Vorlauftemperatur verbessert sich der Temperaturhub und damit der COP.",
          successIndicator: "Ein niedrigerer Vorlauf ist eingestellt."
        },
        {
          id: "5c",
          task: "Heizfläche anpassen",
          instruction: "Passen Sie bei Bedarf Heizkörpertyp oder Größe an.",
          detailedGuidance: "So sichern Sie die notwendige Heizleistung trotz abgesenkter Systemtemperatur.",
          successIndicator: "Leistung und niedriger Vorlauf passen zusammen."
        },
        {
          id: "5d",
          task: "Nutzen quantifizieren",
          instruction: "Bewerten Sie die COP-Verbesserung als Energie- oder Kostenvorteil.",
          detailedGuidance: "Die technische Effizienz wird für Kunden erst als Nutzen greifbar, wenn sie in Verbrauch oder Kosten übersetzt wird.",
          successIndicator: "Der Nutzen der Optimierung ist benannt."
        },
        {
          id: "5e",
          task: "Guten Zielwert erreichen",
          instruction: "Optimieren Sie den Betrieb auf einen hohen COP.",
          detailedGuidance: "Ein hoher COP im Auslegungsfall zeigt ein stimmiges Gesamtsystem.",
          successIndicator: "Ein hoher COP ist erreicht."
        }
      ],
      completionCriteria: {
        description: "Einen optimierten Effizienzpunkt erreichen",
        conditions: [
          "Vorlauftemperatur ≤ 40°C",
          "Heizleistung deckt den Bedarf weitgehend"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Niedriger Vorlauf verbessert die Effizienz",
          "Der COP sollte immer im Gesamtsystem betrachtet werden",
          "Heizflächen und Vorlauftemperatur sind eng gekoppelt"
        ],
        formulaHighlight: "COP_ideal = T_heiß / (T_heiß - T_kalt)"
      }
    },
    {
      id: 6,
      title: "Ganzheitliche Systembewertung",
      category: "Fachliche Analyse",
      difficulty: "Experte",
      learningObjectives: [
        "Gebäudehülle, Lüftung und Systemtemperaturen gemeinsam bewerten",
        "Eine fachlich konsistente Gesamtbeurteilung ableiten",
        "Technische Empfehlungen sauber strukturieren"
      ],
      scenario: {
        context: "Sie erstellen eine vollständige Bewertung für einen Kunden und führen dafür alle relevanten Einflussgrößen zusammen.",
        realWorldConnection: "Eine ganzheitliche Bewertung unterscheidet fundierte Fachberatung von reiner Produktwahl."
      },
      steps: [
        {
          id: "6a",
          task: "Referenzfall setzen",
          instruction: "Stellen Sie den normgerechten Referenzfall ein.",
          detailedGuidance: "Damit erhalten Sie eine belastbare Vergleichsbasis.",
          successIndicator: "Der Referenzfall ist aktiv."
        },
        {
          id: "6b",
          task: "Hülle verbessern",
          instruction: "Stellen Sie eine gute Gebäudehülle ein.",
          detailedGuidance: "Eine niedrige Heizlast ist die Grundlage für niedrige Systemtemperaturen und hohe Effizienz.",
          successIndicator: "Die Hülle ist energetisch günstig eingestellt."
        },
        {
          id: "6c",
          task: "Lüftung bewerten",
          instruction: "Wählen Sie einen plausiblen Luftwechsel.",
          detailedGuidance: "So berücksichtigen Sie den zweiten großen Verlustpfad neben der Hülle.",
          successIndicator: "Die Lüftung ist plausibel eingestellt."
        },
        {
          id: "6d",
          task: "Vorlauf optimieren",
          instruction: "Bringen Sie das System auf eine möglichst niedrige Vorlauftemperatur.",
          detailedGuidance: "Die Kombination aus ausreichender Heizfläche und niedriger Systemtemperatur ist der Kern eines effizienten Wärmepumpenkonzepts.",
          successIndicator: "Ein günstiger Betriebspunkt ist erreicht."
        },
        {
          id: "6e",
          task: "Gesamtwirkung prüfen",
          instruction: "Prüfen Sie Effizienz und Temperaturhaltung gemeinsam.",
          detailedGuidance: "Erst das Zusammenspiel aller Größen zeigt, ob das System fachlich stimmig ausgelegt ist.",
          successIndicator: "Die Gesamtbewertung ist abgeschlossen."
        }
      ],
      completionCriteria: {
        description: "Eine vollständige Systembewertung abschließen",
        conditions: [
          "COP ≥ 4.5",
          "Raumtemperatur stabil nahe dem Sollwert"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Nur die gemeinsame Betrachtung aller Einflussgrößen liefert belastbare Ergebnisse",
          "Hülle, Lüftung und Systemtemperaturen sind untrennbar verknüpft",
          "Eine gute Bewertung endet mit klar priorisierten Maßnahmen"
        ],
        formulaHighlight: "COP_System = f(Hülle, Lüftung, T_Vorlauf, Heizfläche)"
      }
    }
  ],
  salesPerson: [
    {
      id: 2,
      title: "Effizienz der Wärmepumpe verständlich erklären",
      category: "Produktwissen",
      difficulty: "Anfänger",
      learningObjectives: [
        "Den COP kundengerecht erklären",
        "Den Effizienzvorteil einfach darstellen",
        "Technik in verständliche Nutzenargumente übersetzen"
      ],
      scenario: {
        context: "Ein Kunde fragt, warum eine Wärmepumpe effizienter sein soll als sein bisheriges System. Sie möchten das einfach und überzeugend erklären.",
        realWorldConnection: "Im Verkauf ist entscheidend, technische Vorteile in verständliche Kundensprache zu übersetzen."
      },
      steps: [
        {
          id: "2a",
          task: "Simulation starten",
          instruction: "Starten Sie die Simulation für eine anschauliche Live-Demonstration.",
          detailedGuidance: "Eine laufende Darstellung ist für viele Kunden leichter verständlich als reine Zahlen oder Folien.",
          successIndicator: "Die Simulation ist gestartet."
        },
        {
          id: "2b",
          task: "COP zeigen",
          instruction: "Zeigen Sie dem Kunden die COP-Anzeige an der Außeneinheit.",
          detailedGuidance: "Der COP ist die einfachste Kennzahl, um Effizienz sichtbar zu machen.",
          successIndicator: "Der COP ist im Gespräch sichtbar eingebunden."
        },
        {
          id: "2c",
          task: "Kundensprache verwenden",
          instruction: "Erklären Sie den COP in einer leicht verständlichen Nutzenformulierung.",
          detailedGuidance: "Je einfacher der Zusammenhang zwischen Strom und erzeugter Wärme erklärt wird, desto besser versteht der Kunde den Vorteil.",
          successIndicator: "Die Effizienz ist in Kundensprache erklärt."
        },
        {
          id: "2d",
          task: "Einfluss der Betriebsbedingungen zeigen",
          instruction: "Machen Sie sichtbar, dass der COP je nach Außentemperatur und Betriebspunkt variiert.",
          detailedGuidance: "So verstehen Kunden, dass die Wärmepumpe besonders im typischen Alltagsbetrieb hohe Effizienz erreicht.",
          successIndicator: "Die Abhängigkeit des COP von den Randbedingungen ist gezeigt."
        }
      ],
      completionCriteria: {
        description: "Effizienzvorteil verständlich demonstrieren",
        conditions: ["Simulation läuft"]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Der COP ist die zentrale Kennzahl für den Nutzen im Gespräch",
          "Effizienz wird verständlich, wenn sie in Alltagssprache übersetzt wird",
          "Betriebsbedingungen beeinflussen die Wärmepumpenleistung sichtbar"
        ],
        formulaHighlight: "COP = abgegebene Wärme / eingesetzter Strom"
      }
    },
    {
      id: 3,
      title: "Komfort und Regelung zeigen",
      category: "Kundennutzen",
      difficulty: "Anfänger",
      learningObjectives: [
        "Stabile Raumtemperaturen als Komfortvorteil zeigen",
        "Die automatische Regelung erklären",
        "Einfache Bedienung als Nutzen hervorheben"
      ],
      scenario: {
        context: "Ein Kunde möchte wissen, ob eine Wärmepumpe den gleichen Komfort bietet wie sein bisheriges System.",
        realWorldConnection: "Komfort ist oft wichtiger als reine Technik. Wer das überzeugend zeigt, stärkt das Vertrauen des Kunden."
      },
      steps: [
        {
          id: "3a",
          task: "Komfortbedingungen einstellen",
          instruction: "Wählen Sie Wohnen mit 20 °C Sollwert.",
          detailedGuidance: "Damit schaffen Sie einen vertrauten Referenzfall für das Gespräch.",
          successIndicator: "Der Komfortfall ist eingestellt."
        },
        {
          id: "3b",
          task: "Temperaturstabilität zeigen",
          instruction: "Lassen Sie die Simulation laufen und zeigen Sie die stabile Raumtemperatur.",
          detailedGuidance: "Der Kunde sieht, dass das System den Sollwert ohne starke Schwankungen hält.",
          successIndicator: "Die Temperaturstabilität ist sichtbar."
        },
        {
          id: "3c",
          task: "Thermostatfunktion erläutern",
          instruction: "Zeigen Sie, wie der Thermostatkopf die Wärmeabgabe automatisch beeinflusst.",
          detailedGuidance: "Damit wird klar, dass Komfort nicht ständig manuell nachgeregelt werden muss.",
          successIndicator: "Die Thermostatfunktion ist erklärt."
        },
        {
          id: "3d",
          task: "Raumtypen vergleichen",
          instruction: "Wechseln Sie den Raumtyp und erklären Sie unterschiedliche Solltemperaturen.",
          detailedGuidance: "So wird deutlich, dass Komfort je nach Nutzung angepasst werden kann.",
          successIndicator: "Der Zusammenhang zwischen Raumtyp und Sollwert ist gezeigt."
        }
      ],
      completionCriteria: {
        description: "Komfort und Regelbarkeit anschaulich machen",
        conditions: [
          "Raumtyp Wohnen gewählt",
          "Soll-Raumtemperatur = 20°C"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Wärmepumpen können stabile Raumtemperaturen liefern",
          "Die Regelung arbeitet automatisch und komfortorientiert",
          "Komfort lässt sich mit wenig Technikjargon gut vermitteln"
        ],
        formulaHighlight: "Komfort = stabile Temperatur nahe dem Sollwert"
      }
    },
    {
      id: 4,
      title: "Bedenken zur Gebäudehülle entkräften",
      category: "Einwandbehandlung",
      difficulty: "Mittelstufe",
      learningObjectives: [
        "Den Einwand 'zu altes Haus' fachlich einordnen",
        "Den Einfluss der Hülle auf die Wärmepumpentauglichkeit zeigen",
        "Verbesserungsmaßnahmen als Chance darstellen"
      ],
      scenario: {
        context: "Ein Kunde glaubt, sein älteres Gebäude sei grundsätzlich ungeeignet für eine Wärmepumpe. Sie möchten zeigen, dass die Hülle entscheidend ist.",
        realWorldConnection: "Gerade bei häufigen Einwänden helfen anschauliche Zusammenhänge mehr als abstrakte Gegenargumente."
      },
      steps: [
        {
          id: "4a",
          task: "U-Parameter öffnen",
          instruction: "Öffnen Sie die Eingaben zur Gebäudehülle.",
          detailedGuidance: "So verlagern Sie das Gespräch vom Gebäudealter auf die energetische Qualität.",
          successIndicator: "Die U-Parameter sind geöffnet."
        },
        {
          id: "4b",
          task: "Schwache Hülle zeigen",
          instruction: "Stellen Sie einen hohen Wand-U-Wert ein und zeigen Sie den Effekt.",
          detailedGuidance: "Dadurch wird sichtbar, warum hohe Wärmeverluste das eigentliche Problem sind.",
          successIndicator: "Der Einfluss einer schwachen Hülle ist gezeigt."
        },
        {
          id: "4c",
          task: "Verbesserung demonstrieren",
          instruction: "Reduzieren Sie den U-Wert deutlich und zeigen Sie die Verringerung der Verluste.",
          detailedGuidance: "Damit erkennt der Kunde, wie stark die Hülle die Eignung für die Wärmepumpe beeinflusst.",
          successIndicator: "Die Verbesserung der Hülle ist nachvollziehbar."
        },
        {
          id: "4d",
          task: "Wärmestrom sichtbar machen",
          instruction: "Aktivieren Sie das Wärmestrom-Overlay zur Veranschaulichung.",
          detailedGuidance: "Die Visualisierung macht den Unterschied auch ohne Fachjargon verständlich.",
          successIndicator: "Das Overlay unterstützt die Erklärung."
        }
      ],
      completionCriteria: {
        description: "Gebäude-Einwand sachlich entkräften",
        conditions: [
          "Wandfläche etwa 50 m²",
          "Wand-U-Wert etwa 0,2 W/(m²·K)"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Entscheidend ist nicht das Baujahr, sondern die energetische Qualität",
          "Eine bessere Hülle senkt die Heizlast deutlich",
          "Hüllenverbesserungen lassen sich im Gespräch als Zusatznutzen positionieren"
        ],
        formulaHighlight: "Bessere Hülle = geringere Verluste = bessere Wärmepumpenbedingungen"
      }
    },
    {
      id: 5,
      title: "Betriebskosten anschaulich zeigen",
      category: "Wertargumentation",
      difficulty: "Mittelstufe",
      learningObjectives: [
        "Betriebskostenunterschiede verständlich zeigen",
        "Den Einfluss der Vorlauftemperatur erklären",
        "Einfache Kostenargumente aufbauen"
      ],
      scenario: {
        context: "Ein Kunde zweifelt, ob eine Wärmepumpe wegen des Strompreises wirtschaftlich ist. Sie möchten die Kostenwirkung sauber und einfach darstellen.",
        realWorldConnection: "Kostenargumente sind oft kaufentscheidend. Verständliche Vergleiche schaffen Vertrauen."
      },
      steps: [
        {
          id: "5a",
          task: "Vergleichsfall einstellen",
          instruction: "Wählen Sie einen einheitlichen Referenzfall für den Vergleich.",
          detailedGuidance: "Gleiche Randbedingungen sind wichtig, damit Ihr Vergleich glaubwürdig bleibt.",
          successIndicator: "Der Vergleichsfall ist definiert."
        },
        {
          id: "5b",
          task: "Höheren Vorlauf zeigen",
          instruction: "Stellen Sie einen höheren Vorlauf ein und lesen Sie den COP ab.",
          detailedGuidance: "So entsteht die Referenz für einen weniger effizienten Betriebspunkt.",
          successIndicator: "Der COP im Hochtemperaturbetrieb ist bekannt."
        },
        {
          id: "5c",
          task: "Effizienten Betriebspunkt einstellen",
          instruction: "Reduzieren Sie den Vorlauf und sichern Sie die Heizleistung mit passender Heizfläche.",
          detailedGuidance: "Jetzt wird sichtbar, wie sich bessere Auslegung in geringeren Betriebskosten niederschlägt.",
          successIndicator: "Ein effizienter Betriebspunkt ist erreicht."
        },
        {
          id: "5d",
          task: "Kostenwirkung beschreiben",
          instruction: "Übersetzen Sie den COP-Unterschied in einen einfachen Kostenvergleich.",
          detailedGuidance: "Kunden verstehen den Nutzen am besten über Kosten pro nutzbarer Kilowattstunde Wärme.",
          successIndicator: "Ein verständlicher Kostenvergleich ist formuliert."
        },
        {
          id: "5e",
          task: "Milden Betrieb zeigen",
          instruction: "Zeigen Sie, dass die Effizienz bei milderen Außentemperaturen weiter steigt.",
          detailedGuidance: "So wird klar, dass der schlechteste Fall nicht den typischen Jahresbetrieb abbildet.",
          successIndicator: "Der Jahresnutzen ist anschaulich erklärt."
        }
      ],
      completionCriteria: {
        description: "Wirtschaftlichen Nutzen überzeugend zeigen",
        conditions: [
          "Vorlauftemperatur ≤ 40°C",
          "Heizleistung deckt den Bedarf weitgehend"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Der COP lässt sich in Betriebskosten übersetzen",
          "Niedrigere Vorlauftemperaturen verbessern die Wirtschaftlichkeit",
          "Ein einfacher Kostenvergleich ist im Gespräch oft am wirksamsten"
        ],
        formulaHighlight: "Kosten pro kWh Wärme = Strompreis / COP"
      }
    },
    {
      id: 6,
      title: "Komplette Kundendemonstration",
      category: "Verkaufskompetenz",
      difficulty: "Fortgeschritten",
      learningObjectives: [
        "Eine vollständige Demo strukturiert aufbauen",
        "Komfort, Effizienz und Nutzen zusammenführen",
        "Mit einer klaren Schlussbotschaft enden"
      ],
      scenario: {
        context: "Sie führen eine vollständige Vorführung für einen interessierten Kunden durch und möchten die Stärken des Systems klar zusammenfassen.",
        realWorldConnection: "Eine gute Struktur hilft, technische Aspekte in einen überzeugenden Entscheidungsprozess zu übersetzen."
      },
      steps: [
        {
          id: "6a",
          task: "Mit Komfort beginnen",
          instruction: "Zeigen Sie zuerst eine angenehme und stabile Raumtemperatur.",
          detailedGuidance: "Komfort ist meist der beste Einstieg in das Gespräch.",
          successIndicator: "Der Komfort ist sichtbar dargestellt."
        },
        {
          id: "6b",
          task: "Gebäudeeinfluss erklären",
          instruction: "Zeigen Sie, wie die Gebäudehülle das Verhalten des Systems beeinflusst.",
          detailedGuidance: "So adressieren Sie früh die Frage, ob das System zum Gebäude des Kunden passt.",
          successIndicator: "Der Gebäudeeinfluss ist nachvollziehbar erklärt."
        },
        {
          id: "6c",
          task: "Lüftung einordnen",
          instruction: "Beziehen Sie die Lüftung in die Erklärung des Gesamtverhaltens ein.",
          detailedGuidance: "Das zeigt Fachkompetenz und einen ganzheitlichen Blick.",
          successIndicator: "Der Einfluss der Lüftung ist erklärt."
        },
        {
          id: "6d",
          task: "Effizienten Betriebspunkt zeigen",
          instruction: "Stellen Sie einen guten Effizienzpunkt mit niedriger Vorlauftemperatur ein.",
          detailedGuidance: "Hier verbinden sich Technik, Komfort und Kostenargumentation.",
          successIndicator: "Ein effizienter Betriebspunkt ist sichtbar."
        },
        {
          id: "6e",
          task: "Mit Nutzen abschließen",
          instruction: "Fassen Sie Komfort, Effizienz und Wirtschaftlichkeit in einer klaren Nutzenbotschaft zusammen.",
          detailedGuidance: "Ein gutes Ende verdichtet die technischen Inhalte zu einer verständlichen Entscheidungshilfe.",
          successIndicator: "Die Demonstration endet mit einer klaren Nutzenbotschaft."
        }
      ],
      completionCriteria: {
        description: "Eine vollständige Kundendemonstration sicher führen",
        conditions: [
          "COP ≥ 4.5",
          "Raumtemperatur stabil nahe dem Sollwert"
        ]
      },
      knowledgeSynthesis: {
        keyTakeaways: [
          "Eine gute Demo verbindet Komfort, Technik und Nutzen",
          "Struktur und Klarheit sind im Kundengespräch entscheidend",
          "Am Ende zählt eine verständliche Schlussbotschaft"
        ],
        formulaHighlight: "Überzeugung = Komfort + Effizienz + klarer Nutzen"
      }
    }
  ]
};